import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, inject, input, InputSignal, ResourceRef, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { PageHeaderComponent } from '@libs/common/components';
import { ProductDetailHeaderComponent, ProductDetailInfoComponent } from '@libs/shop/components';
import { ProductModel} from '@libs/shop/models';
import { ProductDataModel, ProductSearchResultModel } from '@libs/shop/models';
import { LmivService } from '@libs/shared/services';
import { DeferredPageInfoTracking } from '@libs/shared/tracking';
import { Observable } from 'rxjs';
import { LmivProductSkeletonComponent } from './lmiv-product-skeleton';
import { LmivProductSearchComponent } from './lmiv-product-search';
import { LmivProductNotFoundComponent } from './lmiv-product-not-found';

/* eslint-disable @typescript-eslint/member-ordering */

@Component({
  selector: 'ui-lmiv-product',
  standalone: true,
  templateUrl: './lmiv-product.component.html',
  styleUrls: ['./lmiv-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageHeaderComponent,
    ProductDetailInfoComponent,
    LmivProductSearchComponent,
    LmivProductSkeletonComponent,
    LmivProductNotFoundComponent,
    ProductDetailHeaderComponent,
  ],
  providers: [
    DeferredPageInfoTracking,
  ],
})
export class LmivProductComponent {
  private readonly lmivService: LmivService = inject(LmivService);
  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);

  /** Name correlates to query-parameter for the route */
  public readonly q: InputSignal<string | undefined> = input<string| undefined>('');
  public readonly searchText: Signal<string> = computed((): string => this.q() ?? '');

  public readonly productResource: ResourceRef<ProductSearchResultModel | undefined> = rxResource({
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    params: (): string | undefined => this.q()?.trim() || undefined,
    stream: ({ params }: { params: string }): Observable<ProductSearchResultModel | undefined> =>
      this.lmivService.searchProduct(params),
  });

  public readonly searchResult: Signal<ProductSearchResultModel | undefined> = computed(
    (): ProductSearchResultModel | undefined => this.productResource.value(),
  );

  public readonly product: Signal<ProductDataModel | undefined> = computed(
    (): ProductDataModel | undefined => this.searchResult()?.product,
  );

  public readonly productNotFound: Signal<boolean> = computed((): boolean => {
    const result: ProductSearchResultModel | undefined = this.searchResult();

    return result !== undefined && result.count !== 1;
  });

  public readonly productModel: Signal<ProductModel | undefined> = computed((): ProductModel | undefined => {
    const data: ProductDataModel | undefined = this.product();

    if (!data) {
      return undefined;
    }

    return {
      sku: this.searchText(),
      details: {
        name: data.name ?? '',
        manufacturer: data.manufacturer ?? '',
        packagingUnit: '',
        description: data.description,
      },
      images: data.images,
      categories: [],
      data,
      list: false,
      isEsa: false,
      isFree: false,
    };
  });

  private readonly trackingEffect: EffectRef = effect((): void => {
    const searchTerm: string = this.searchText();

    // Without a search term there is nothing to look up, so the page is complete as it is.
    if (!searchTerm) {
      this.deferredPageInfoTracking.complete();

      return;
    }

    if (!this.productResource.hasValue()) {
      return;
    }

    const result: ProductSearchResultModel = this.productResource.value();

    this.deferredPageInfoTracking.complete({
      searchTerm,
      numSearchResults: result.count,
      productId: result.product?.articleNumber ?? null
    });
  });
}
